/**
 * Return-to after sign-in (ZOY-610). A signed-out visit to an authenticated
 * route remembers WHERE it wanted to go — in this tab only — and the sign-in
 * that follows continues there. Two rules keep it honest:
 *  - only an internal, authenticated app path qualifies (never a public
 *    route, never a foreign origin, never the entry-URL family);
 *  - it belongs to the person who started it: the tab notes which uid's
 *    session ended (null for a cold deep link), and a different uid signing
 *    in discards it rather than being taken into someone else's patient.
 */

const NEXT_KEY = 'zc.next';
const LAST_UID_KEY = 'zc.lastUid';

/** Public / non-returnable path families (exact, or as a path prefix). */
const NOT_RETURNABLE = ['/signin', '/forgot', '/c'];

export function sanitizeNext(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  if (v === '' || v === '/' || v.length > 600) return null;
  if (!v.startsWith('/') || v.startsWith('//') || v.startsWith('/\\')) return null;
  if (/[\r\n]/.test(v)) return null;
  const path = v.split('?')[0]!.split('#')[0]!;
  if (NOT_RETURNABLE.some((p) => path === p || path.startsWith(p + '/'))) return null;
  return v;
}

function read<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

/** The uid whose session last ended in this tab (null = none / cold start). */
export function noteSessionEnded(uid: string): void {
  try {
    sessionStorage.setItem(LAST_UID_KEY, JSON.stringify(uid));
  } catch {
    /* ignore */
  }
}

interface Intent {
  path: string;
  /** Who was signed in when the intent was recorded; null for a cold link. */
  forUid: string | null;
}

/** Remember where a signed-out visit wanted to go. Not returnable → nothing stored. */
export function rememberIntent(rawPath: string): void {
  const path = sanitizeNext(rawPath);
  try {
    if (path === null) return;
    const forUid = read<string>(LAST_UID_KEY);
    sessionStorage.setItem(NEXT_KEY, JSON.stringify({ path, forUid } satisfies Intent));
  } catch {
    /* ignore */
  }
}

/** The path the signed-in `uid` may continue to — theirs, or a cold link's. */
export function peekIntent(uid: string): string | null {
  const i = read<Intent>(NEXT_KEY);
  if (!i) return null;
  if (i.forUid !== null && i.forUid !== uid) return null;
  return sanitizeNext(i.path);
}

/** Consume: the intent is used once, and a foreign one is discarded outright. */
export function takeIntent(uid: string): string | null {
  const path = peekIntent(uid);
  try {
    sessionStorage.removeItem(NEXT_KEY);
  } catch {
    /* ignore */
  }
  return path;
}
